import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { parseUploadedFile } from './parseUploadedFile.js';

const UploadCard = ({
  title,
  subtitle,
  source,
  onParsed,
  onClear,
  currentFile,
  rowCount,
  seeded,
  sampleAvailable = false,
  serverParsed = false,
}) => {
  const [error, setError] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    setError(null);
    setParsing(true);
    try {
      const result = await parseUploadedFile(file, source);
      onParsed({ ...result, file });
    } catch (e) {
      setError(e.message || 'Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  const onChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const hasFile = Boolean(currentFile);

  return (
    <Paper
      variant="outlined"
      onDragOver={(e) => {
        e.preventDefault();
        if (!dragActive) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDrop}
      sx={{
        p: 2.5,
        flex: 1,
        minWidth: 0,
        borderRadius: 1.5,
        position: 'relative',
        overflow: 'hidden',
        borderStyle: hasFile ? 'solid' : 'dashed',
        borderWidth: dragActive ? 2 : 1,
        borderColor: dragActive
          ? 'primary.main'
          : hasFile
            ? 'success.main'
            : 'divider',
        bgcolor: dragActive
          ? (t) => (t.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)')
          : 'background.paper',
        transition: 'border-color 120ms, background-color 120ms, border-width 120ms',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: -0.2 }} noWrap>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, display: 'block' }}>
              {subtitle}
            </Typography>
          </Box>
          {hasFile ? (
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label="Loaded"
              color="success"
              sx={{ fontWeight: 600, height: 22 }}
            />
          ) : seeded ? (
            <Chip
              size="small"
              label="Sample"
              variant="outlined"
              sx={{ fontWeight: 600, height: 22, color: 'info.main', borderColor: 'info.main' }}
            />
          ) : null}
        </Stack>

        {!hasFile ? (
          <Stack
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{
              py: 3.5,
              borderRadius: 1.25,
              bgcolor: dragActive
                ? (t) => (t.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.06)')
                : 'action.hover',
              cursor: 'pointer',
              transition: 'background-color 120ms, transform 120ms',
              '&:hover': { bgcolor: 'action.selected' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            <CloudUploadOutlinedIcon
              sx={{
                fontSize: 40,
                color: dragActive ? 'primary.main' : 'text.secondary',
                opacity: dragActive ? 1 : 0.75,
                transition: 'color 120ms, opacity 120ms, transform 160ms',
                transform: dragActive ? 'scale(1.08)' : 'scale(1)',
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {parsing
                ? 'Parsing…'
                : dragActive
                  ? 'Drop to upload'
                  : 'Click or drop a file'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {seeded ? 'Sample data loaded - upload to replace' : 'Accepts .csv, .txt, and .xlsx'}
            </Typography>
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              p: 1.5,
              borderRadius: 1.25,
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.12)' : 'rgba(46, 125, 50, 0.06)',
              border: '1px solid',
              borderColor: 'success.light',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: 'success.main',
                color: 'success.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                {currentFile}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {serverParsed ? 'Workbook will be parsed by the API' : `${rowCount.toLocaleString()} rows parsed`}
              </Typography>
            </Box>
            <Tooltip title="Remove uploaded file">
              <IconButton size="small" onClick={onClear} aria-label="Remove uploaded file">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant={hasFile ? 'outlined' : 'contained'}
            size="small"
            onClick={() => inputRef.current?.click()}
            disabled={parsing}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {hasFile ? 'Replace file' : 'Choose file'}
          </Button>
          {hasFile && (
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={onClear}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {sampleAvailable ? 'Revert to sample' : 'Clear file'}
            </Button>
          )}
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        <input
          ref={inputRef}
          type="file"
          accept=".csv,.txt,.xlsx"
          onChange={onChange}
          hidden
        />
      </Stack>
    </Paper>
  );
};

export default UploadCard;
